import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { usePublicData } from '../lib/hooks';
import { fmtDateTime, inr, KIND_LABEL, mealName, seats, serveDateText, windowText } from '../lib/format';
import type { CartLine, MenuItem, Sitting } from '../lib/types';
import { Stepper, VegMark } from './ui';

export type Cart = Record<string, { adults: number; children: number }>;
const KIND_ORDER = { veg: 0, nonveg: 1, common: 2 } as const;

/** One event's menu grouped by day and sitting, plus lookups. Meals of other events are left out (an old cart too). */
export function useMenu(eventId: string | null) {
  const { sittings, menu, settings, venues, events } = usePublicData();
  const event = events.data?.find((e) => e.id === eventId) ?? null;
  const eventSittings = useMemo(() => (sittings.data ?? []).filter((s) => s.event_id === eventId), [sittings.data, eventId]);
  const days = useMemo(() => {
    const out: { key: string; label: string; bn: string; date: string; sittings: Sitting[] }[] = [];
    for (const s of eventSittings) {
      const last = out[out.length - 1];
      if (last && last.key === s.day_key) last.sittings.push(s);
      else out.push({ key: s.day_key, label: s.day_label, bn: s.day_label_bn, date: serveDateText(s), sittings: [s] });
    }
    return out;
  }, [eventSittings]);
  const itemsBySitting = useMemo(() => {
    const m = new Map<string, MenuItem[]>();
    for (const it of menu.data ?? []) {
      const list = m.get(it.sitting_id) ?? [];
      list.push(it);
      m.set(it.sitting_id, list);
    }
    m.forEach((list) => list.sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]));
    return m;
  }, [menu.data]);
  const menuMap = useMemo(() => new Map((menu.data ?? []).map((m) => [m.id, m])), [menu.data]);
  const sittingMap = useMemo(() => new Map(eventSittings.map((s) => [s.id, s])), [eventSittings]);
  const venueMap = useMemo(() => new Map((venues.data ?? []).map((v) => [v.id, v])), [venues.data]);
  return {
    days, itemsBySitting, menuMap, sittingMap, venueMap, settings, event,
    isPending: sittings.isPending || menu.isPending || events.isPending,
    error: sittings.error ?? menu.error ?? events.error,
  };
}

export function setCartQty(cart: Cart, id: string, field: 'adults' | 'children', v: number): Cart {
  const cur = cart[id] ?? { adults: 0, children: 0 };
  const nextQ = { ...cur, [field]: Math.max(0, Math.min(20, v)) };
  const out = { ...cart, [id]: nextQ };
  if (nextQ.adults + nextQ.children === 0) delete out[id];
  return out;
}

/** Cart lines that can still be ordered, sorted by meal, with totals. */
export function cartSummary(cart: Cart, m: ReturnType<typeof useMenu>, isClosed: (s: Sitting) => boolean) {
  const lines = Object.entries(cart)
    .filter(([id, q]) => {
      const it = m.menuMap.get(id);
      const s = it && m.sittingMap.get(it.sitting_id);
      return it && s && !isClosed(s) && q.adults + q.children > 0;
    })
    .map(([id, q]) => ({ item: m.menuMap.get(id)!, ...q }))
    .sort((a, b) => (m.sittingMap.get(a.item.sitting_id)!.sort - m.sittingMap.get(b.item.sitting_id)!.sort) || KIND_ORDER[a.item.kind] - KIND_ORDER[b.item.kind]);
  return {
    lines,
    total: lines.reduce((t, l) => t + l.adults * l.item.adult_price + l.children * l.item.child_price, 0),
    plates: lines.reduce((t, l) => t + l.adults + l.children, 0),
    payload: lines.map((l): CartLine => ({ menu_item_id: l.item.id, adults: l.adults, children: l.children })),
  };
}

export function CartLines({ lines, m }: { lines: ReturnType<typeof cartSummary>['lines']; m: ReturnType<typeof useMenu> }) {
  return (
    <ul className="olines">
      {lines.map((l) => {
        const s = m.sittingMap.get(l.item.sitting_id)!;
        const sub = l.adults * l.item.adult_price + l.children * l.item.child_price;
        return (
          <li key={l.item.id}>
            <span>
              <span><VegMark kind={l.item.kind} />{s.day_label} · {mealName(s)}{l.item.kind !== 'common' ? ` · ${KIND_LABEL[l.item.kind]}` : ''}</span>
              <small>{seats(l.adults, l.children)}</small>
            </span>
            <b>{sub ? inr(sub) : 'Free'}</b>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Day tabs and meal cards with adult/child steppers. flatId: the family the Food Committee books for (else the signed-in
 * one). guest: nobody signed in (or no family yet), so there is no flat to count free Bhog seats for.
 */
export function MenuPicker({ m, cart, onChange, isClosed, closedText, disabled, flatId, guest }: {
  m: ReturnType<typeof useMenu>;
  cart: Cart;
  onChange: (cart: Cart) => void;
  isClosed: (s: Sitting) => boolean;
  closedText: (s: Sitting) => string;
  disabled?: boolean;
  flatId?: string;
  guest?: boolean;
}) {
  const [day, setDay] = useState<string | null>(null);
  const selectedDay = day ?? m.days.find((d) => d.sittings.some((s) => !isClosed(s)))?.key ?? m.days[0]?.key;
  const dayData = m.days.find((d) => d.key === selectedDay);
  // Free Bhog seats are limited per flat and event, counting every family at the flat.
  const hasBhog = m.days.some((d) => d.sittings.some((s) => (m.itemsBySitting.get(s.id) ?? []).some((it) => it.is_free)));
  const bhog = useQuery({
    queryKey: ['bhog', m.event?.id, flatId ?? 'mine'], queryFn: () => api.bhogUsed(m.event!.id, flatId),
    enabled: !!m.event && hasBhog && !guest, staleTime: 0,
  });
  const b = bhog.data;
  const cap = b?.cap ?? m.event?.bhog_cap_per_flat ?? m.settings.data?.bhog_cap_per_flat ?? 10;
  const left = b ? Math.max(0, b.cap - b.mine - b.others) : null;
  const inCart = Object.entries(cart).reduce((t, [id, q]) => t + (m.menuMap.get(id)?.is_free ? q.adults + q.children : 0), 0);
  const whose = flatId ? 'this flat' : 'your flat';
  return (
    <>
      {b && b.others > 0 && (
        <div className="notice">
          <p><b>Free Bhog: {left} of {b.cap} seats left for {whose}.</b> {b.others === 1 ? 'One seat was' : `${b.others} seats were`} booked by
            another family at {whose}{b.mine ? `, ${b.mine} by ${flatId ? 'this family' : 'yours'}` : ''}.</p>
          <p className="fine">Families at one flat share its Bhog limit. If someone joined {whose} by mistake, ask the help desk: once they move
            them to their own flat, those seats come back.</p>
        </div>
      )}
      {left !== null && inCart > left && (
        <p className="err">You picked {inCart} free Bhog {inCart === 1 ? 'seat' : 'seats'}; only {left} {left === 1 ? 'is' : 'are'} left for {whose} in this event.</p>
      )}
      <div className="daytabs" role="tablist" aria-label={`${m.event?.name ?? 'Event'} days`}>
        {m.days.map((d) => (
          <button key={d.key} type="button" role="tab" className="daytab" aria-selected={d.key === selectedDay} onClick={() => setDay(d.key)}>
            <b><span style={{ color: 'var(--sindoor-text)' }}>{d.bn || d.label}</span></b>
            <small>{d.bn ? `${d.label} · ` : ''}{d.date}</small>
          </button>
        ))}
      </div>
      {dayData?.sittings.map((s) => {
        const closed = isClosed(s) || !!disabled;
        return (
          <section key={s.id} className="stack">
            <div className="sitting-h">
              <h3>{mealName(s)}</h3>
              <span className="fine">{windowText(s)} · {closedText(s)}</span>
            </div>
            {(m.itemsBySitting.get(s.id) ?? []).map((it) => {
              const q = cart[it.id] ?? { adults: 0, children: 0 };
              const picked = q.adults + q.children > 0;
              return (
                <article key={it.id} className={`mcard${picked ? ' picked' : ''}${closed ? ' closed' : ''}`}>
                  <div className="mcard-top">
                    <span className="mcard-kind"><VegMark kind={it.kind} />{it.kind === 'common' ? (it.is_free ? 'Bhog (free)' : 'For all') : KIND_LABEL[it.kind]}</span>
                    <span className="mcard-price">
                      {it.is_free ? (left === null ? `Up to ${cap} per flat` : `${Math.max(0, left - inCart)} of ${cap} left for ${whose}`)
                        : `${inr(it.adult_price)} adult · ${inr(it.child_price)} child`}
                    </span>
                  </div>
                  <p className="mcard-items">{it.items.join(' · ')}</p>
                  {m.venueMap.get(it.venue_id) && <p className="mcard-venue">Served at <b>{m.venueMap.get(it.venue_id)!.name}</b></p>}
                  <div className="qty-row">
                    <div className="qty"><span className="qty-l">Adult</span>
                      <Stepper label={`adults, ${s.day_label} ${mealName(s)} ${KIND_LABEL[it.kind]}`} value={q.adults} max={20} disabled={closed}
                        onChange={(v) => onChange(setCartQty(cart, it.id, 'adults', v))} />
                    </div>
                    <div className="qty"><span className="qty-l">Child</span>
                      <Stepper label={`children, ${s.day_label} ${mealName(s)} ${KIND_LABEL[it.kind]}`} value={q.children} max={20} disabled={closed}
                        onChange={(v) => onChange(setCartQty(cart, it.id, 'children', v))} />
                    </div>
                  </div>
                </article>
              );
            })}
          </section>
        );
      })}
    </>
  );
}

export const bookByText = (s: Sitting, closed: boolean) => (closed ? 'Booking closed' : `Book by ${fmtDateTime(s.booking_closes_at)}`);
